import { DataRow } from './DataRow';

import { useSelector } from 'react-redux';
import { usePolicyInsightContext } from './PolicyInsight';

import { selectClient } from '@client/model';
import { formatCurrencyValue } from './policyInsightUtils';
import {
  checkPolicyCalculationTypeEquals,
  FundingPolicyType,
  hasFlatDeathBenefit,
  isNullOrUndefined,
} from '@shared';

export function DeathBenefit() {
  const client = useSelector(selectClient);
  const { ltcPolicy } = usePolicyInsightContext();

  const {
    intakeSurvey: { clientHasStartedLtc },
  } = client;

  const {
    policyInflatedDeathBenefitAmount,
    policyCoverageOfRemainingLtcCosts,
    policyMinimumGuaranteedBenefitAmount,
    indemnitySurplus: totalIndemnitySurplus,
    projectedCostCoverage,
    policyMaximumBenefitAmount,
    policyType,
  } = ltcPolicy;

  // Only show for specific policy types
  const shouldShowDeathBenefit = checkPolicyCalculationTypeEquals(policyType, [
    FundingPolicyType.hybridLifeInsurance,
    FundingPolicyType.lifeInsuranceWithRider,
    FundingPolicyType.hybridLifeIra,
  ]);

  if (!shouldShowDeathBenefit) {
    return null;
  }

  const appliedDeathBenefit = hasFlatDeathBenefit(policyType)
    ? policyMaximumBenefitAmount
    : policyInflatedDeathBenefitAmount;

  const totalPolicyCoverage = clientHasStartedLtc
    ? policyCoverageOfRemainingLtcCosts
    : projectedCostCoverage;

  const policyCoveragePlusIndemnitySurplus =
    (totalPolicyCoverage ?? 0) + (totalIndemnitySurplus ?? 0);

  const remainingDeathBenefit =
    appliedDeathBenefit && !isNullOrUndefined(policyCoveragePlusIndemnitySurplus)
      ? appliedDeathBenefit - policyCoveragePlusIndemnitySurplus
      : 0;

  const isPayoutGreaterThanDeathBenefit =
    appliedDeathBenefit &&
    policyCoveragePlusIndemnitySurplus &&
    policyCoveragePlusIndemnitySurplus >= appliedDeathBenefit;

  const expectedDeathBenefitPayout = isPayoutGreaterThanDeathBenefit
    ? (policyMinimumGuaranteedBenefitAmount ?? 0)
    : policyMinimumGuaranteedBenefitAmount &&
        policyMinimumGuaranteedBenefitAmount > remainingDeathBenefit
      ? policyMinimumGuaranteedBenefitAmount
      : remainingDeathBenefit;

  const getRationale = () => {
    if (isPayoutGreaterThanDeathBenefit) {
      return "Total policy payout is greater than or equal to the Death Benefit amount, so only pay out is the policy's minimum guaranteed death benefit.";
    }

    if (
      policyMinimumGuaranteedBenefitAmount &&
      policyMinimumGuaranteedBenefitAmount > remainingDeathBenefit
    ) {
      return "Since the total policy payout is less than the Death Benefit amount, the amount that will be paid to beneficiaries is the policy's minimum guaranteed death benefit, since this amount is greater than the remaining Death Benefit amount.";
    }

    return "Since the total policy payout is less than the Death Benefit amount, the amount that will be paid to beneficiaries is the remaining Death Benefit amount, since this amount is greater than the policy's minimum guaranteed death benefit.";
  };

  return (
    <section>
      <h2 className="mb-4 text-lg font-semibold text-gray-950">Death Benefit</h2>
      <div className="space-y-4 rounded-xl border border-gray-200 p-4">
        <DataRow label="Initial death benefit amount (plus any inflation protection)">
          {formatCurrencyValue(appliedDeathBenefit)}
        </DataRow>
        <hr className="border-gray-200" />
        <DataRow label="Total policy payout">
          {formatCurrencyValue(policyCoveragePlusIndemnitySurplus)}
        </DataRow>
        <hr className="border-gray-200" />
        <DataRow label="Remaining death benefit amount">
          {formatCurrencyValue(Math.max(remainingDeathBenefit, 0))}
        </DataRow>
        <hr className="border-gray-200" />
        <DataRow label="Minimum guaranteed death benefit">
          {formatCurrencyValue(policyMinimumGuaranteedBenefitAmount ?? 0)}
        </DataRow>
        {!isPayoutGreaterThanDeathBenefit && (
          <>
            <hr className="border-gray-200" />
            <DataRow
              label="Expected death benefit payout"
              highlight="green"
            >
              {formatCurrencyValue(expectedDeathBenefitPayout)}
            </DataRow>
            <p className="text-sm italic text-gray-700">
              This amount is added to the <strong>Non-LTC Payout</strong>.
            </p>
          </>
        )}
        <hr className="border-gray-200" />
        <p className="text-sm italic text-gray-700">{getRationale()}</p>
      </div>
    </section>
  );
}
